/*
 * How the skins shop draws an item's live preview (ENG-894): its type's
 * label, and an FX item's tint for the lightweight CSS preview that plays on
 * tiles and drop cards (the real FX engine plays only in the large try-on
 * preview, one at a time).
 */
import { registeredFxPackage } from './fxPackages.js';
import { FX_PACKAGE_TYPE } from './fxItems.js';

/** The locale key of each item type's name, singular ("Now-playing card", "Vibe set"). */
export const SHOP_TYPE_LABEL_KEY = Object.freeze({
  now_playing_card: 'shop.type.nowPlayingCard',
  name_paint: 'shop.type.namePaint',
  vibe_set: 'stream.fxItem.type.vibeSet',
  boost_animation: 'stream.fxItem.type.boostAnimation',
});

/** An item's type in words; empty for a type the shop doesn't name. */
export const shopTypeLabel = (t, skin) => {
  const key = SHOP_TYPE_LABEL_KEY[skin?.type];
  return key ? t(key) : '';
};

/** A skin's rarity as its label key: "Standard", "Rare", "Epic", "Legendary". */
export const rarityLabelKey = (rarity) => `stream.chat.card.tag.${rarity === 'common' || !rarity ? 'standard' : rarity}`;

/** A skin's rarity as its tag modifier (`listener-card-tag--*`). */
export const rarityTagKind = (rarity) => (rarity === 'common' || !rarity ? 'standard' : rarity);

const HEX = /^#[0-9a-f]{6}$/i;

/** A colour from a package setting: a #RRGGBB, or the strongest step of a per-level list. */
const settingHex = (value) => {
  const v = Array.isArray(value) ? value[value.length - 1] : value;
  return typeof v === 'string' && HEX.test(v) ? v : null;
};

/** The tint when the package isn't loaded (yet): the rarity's colours. */
const RARITY_TINT = {
  common: ['#E7E4DD', '#8A817C'],
  rare: ['#57B8FF', '#A98BFF'],
  epic: ['#A98BFF', '#FF6FA8'],
  legendary: ['#FF8F3F', '#FFE78D'],
};

/**
 * An FX item's two tint colours `{ a, b }`, from its registered package when
 * it is loaded: a vibe set's strongest inner and outer glow, a boost's plume
 * and burst. Until then (or for a package without them) the rarity's colours.
 */
export const fxTint = (skin) => {
  const type = FX_PACKAGE_TYPE[skin?.type];
  const settings = type ? registeredFxPackage(skin.fxPackageId, type)?.settings : null;
  let a = null;
  let b = null;
  if (settings && type === 'vibe') {
    a = settingHex(settings.glowInner);
    b = settingHex(settings.glowOuter);
  } else if (settings && type === 'boost') {
    a = settingHex(settings.plumeOuter) ?? settingHex(settings.burstEndColor);
    b = settingHex(settings.burstColor) ?? settingHex(settings.plumeCore);
  }
  const fallback = RARITY_TINT[skin?.rarity] ?? RARITY_TINT.common;
  return { a: a ?? fallback[0], b: b ?? fallback[1] };
};
